import { PDFDocument, rgb } from 'pdf-lib'
import fontkit from '@pdf-lib/fontkit'

const PAGE_W = 595, PAGE_H = 842
const MARGIN = 36
const BLUE = rgb(0.094, 0.373, 0.647)
const GREY = rgb(0.45, 0.45, 0.45)
const LIGHT_GREY = rgb(0.85, 0.85, 0.85)
const BLACK = rgb(0.1, 0.1, 0.1)
const ROW_H = 32
const FOOTER_RESERVE = 70
const LOGO_SIZE = 50

function formatDOB(dob) {
  if (!dob) return ''
  const d = new Date(dob)
  if (isNaN(d)) return ''
  return String(d.getUTCDate()).padStart(2, '0') + '/' + String(d.getUTCMonth() + 1).padStart(2, '0') + '/' + d.getUTCFullYear()
}

/**
 * Generate a Foireann-style team sheet PDF (Irish-labeled, matching the
 * official Foireann layout as closely as practical).
 *
 * @param {Object} params
 * @param {Object} params.club - { club_name, chairman: {name_en, name_ga}, secretary: {name_en, name_ga} }
 * @param {Object} params.squad - { name }
 * @param {Object} params.fixture - { opponent, competition, date }
 * @param {Array}  params.players - [{ full_name, full_name_irish, date_of_birth, foireann_id, is_guest, selected, starting, jersey_number }]
 * @param {Array}  params.managers - [{ full_name, full_name_irish, foireann_id }]
 * @param {Uint8Array} params.fontRegular - DejaVuSans.ttf bytes
 * @param {Uint8Array} params.fontBold - DejaVuSans-Bold.ttf bytes
 * @param {Uint8Array|null} params.clubLogoBytes - optional club crest image bytes
 * @param {string} params.clubLogoType - 'png' or 'jpg'
 * @param {Uint8Array|null} params.gaaLogoBytes - optional GAA crest image bytes (top-right)
 * @param {string} params.gaaLogoType - 'png' or 'jpg'
 * @returns {Promise<Uint8Array>} PDF bytes
 */
export async function generateTeamSheetPdf({
  club, squad, fixture, players, managers,
  fontRegular, fontBold,
  clubLogoBytes = null, clubLogoType = 'png',
  gaaLogoBytes = null, gaaLogoType = 'png'
}) {
  const pdfDoc = await PDFDocument.create()
  pdfDoc.registerFontkit(fontkit)
  const font = await pdfDoc.embedFont(fontRegular)
  const fontB = await pdfDoc.embedFont(fontBold)

  async function tryEmbed(bytes, type) {
    if (!bytes) return null
    try {
      return type === 'jpg' || type === 'jpeg' ? await pdfDoc.embedJpg(bytes) : await pdfDoc.embedPng(bytes)
    } catch (e) {
      return null
    }
  }
  const clubLogoImg = await tryEmbed(clubLogoBytes, clubLogoType)
  const gaaLogoImg = await tryEmbed(gaaLogoBytes, gaaLogoType)

  // Full real squad always shown; guests only shown if selected for this fixture
  const sheetPlayers = (players || []).filter(p => !p.is_guest || p.selected)

  const pages = []
  let page, y

  function addPage() {
    page = pdfDoc.addPage([PAGE_W, PAGE_H])
    pages.push(page)
    y = PAGE_H - MARGIN
  }
  addPage()

  function text(str, x, yy, opts = {}) {
    page.drawText(str || '', { x, y: yy, size: opts.size || 9, font: opts.bold ? fontB : font, color: opts.color || BLACK })
  }

  function drawHeader(continued) {
    if (clubLogoImg && !continued) {
      const dims = clubLogoImg.scale(1)
      const scale = LOGO_SIZE / Math.max(dims.width, dims.height)
      page.drawImage(clubLogoImg, { x: MARGIN, y: y - LOGO_SIZE + 10, width: dims.width * scale, height: dims.height * scale })
    }
    if (gaaLogoImg && !continued) {
      const dims = gaaLogoImg.scale(1)
      const scale = LOGO_SIZE / Math.max(dims.width, dims.height)
      page.drawImage(gaaLogoImg, { x: PAGE_W - MARGIN - dims.width * scale, y: y - LOGO_SIZE + 10, width: dims.width * scale, height: dims.height * scale })
    }

    const titleX = clubLogoImg && !continued ? MARGIN + LOGO_SIZE + 14 : MARGIN
    text((club?.club_name || club?.name || 'Club') + (continued ? ' (ar lean)' : ''), titleX, y, { size: 19, bold: true, color: BLUE })
    y -= 26
    if (!continued) {
      text(squad?.name || '', MARGIN, y, { size: 12, bold: true })
      text('(Foireann)', MARGIN, y - 10, { size: 7, color: GREY })
      text(fixture?.opponent || '', 320, y, { size: 12, bold: true })
      text('(Freasúra)', 320, y - 10, { size: 7, color: GREY })
      y -= 28
      const chairmanName = club?.chairman?.name_ga?.trim() || club?.chairman?.name_en || ''
      const secretaryName = club?.secretary?.name_ga?.trim() || club?.secretary?.name_en || ''
      text(chairmanName, MARGIN, y, { size: 10 })
      text('(Cathaoirleach)', MARGIN, y - 10, { size: 7, color: GREY })
      text(secretaryName, 320, y, { size: 10 })
      text('(Rúnaí)', 320, y - 10, { size: 7, color: GREY })
      y -= 26
      text(fixture?.competition || '', MARGIN, y, { size: 10 })
      text('(Comórtas)', MARGIN, y - 10, { size: 7, color: GREY })
      text(fixture?.date || '', 320, y, { size: 10 })
      text('(Dáta)', 320, y - 10, { size: 7, color: GREY })
      y -= 22
    } else {
      y -= 4
    }
    page.drawLine({ start: { x: MARGIN, y }, end: { x: PAGE_W - MARGIN, y }, thickness: 0.6, color: LIGHT_GREY })
    y -= 14
    text('☑ = ag tosú (Starting)', MARGIN, y, { size: 8, color: GREY })
    text('Imreoirí', PAGE_W / 2 - 22, y + 1, { size: 13, bold: true })
    y -= 18
  }

  drawHeader(false)

  const colWidth = (PAGE_W - MARGIN * 2 - 24) / 2
  const col1X = MARGIN
  const col2X = MARGIN + colWidth + 24

  function drawPlayerRow(p, x, yy) {
    page.drawRectangle({ x, y: yy - 8, width: 9, height: 9, borderColor: GREY, borderWidth: 0.7 })
    if (p.starting) {
      page.drawLine({ start: { x: x + 1.5, y: yy - 4.5 }, end: { x: x + 3.3, y: yy - 6.8 }, thickness: 1.1, color: BLUE })
      page.drawLine({ start: { x: x + 3.3, y: yy - 6.8 }, end: { x: x + 7.5, y: yy - 1 }, thickness: 1.1, color: BLUE })
    }

    // Single dedicated number column beside the checkbox — pre-filled if known, otherwise
    // left blank with an underline for the coach to write it in by hand on match day
    const NUM_COL_WIDTH = 28
    const numColX = x + 14
    page.drawLine({ start: { x: numColX, y: yy - 18 }, end: { x: numColX + NUM_COL_WIDTH - 6, y: yy - 18 }, thickness: 0.6, color: GREY })
    if (p.jersey_number) {
      text(String(p.jersey_number), numColX + 2, yy - 14, { size: 13, bold: true, color: BLUE })
    }

    const nameX = x + 14 + NUM_COL_WIDTH
    // Irish name primary always; English name always shown below, even if identical (matches Foireann's own format)
    const irishName = p.full_name_irish?.trim() || p.full_name
    text(irishName, nameX, yy, { size: 9, bold: true })

    const dobStr = formatDOB(p.date_of_birth)
    const idDisplay = p.foireann_id || (p.is_guest ? '__________' : '')
    const metaLine = [dobStr, idDisplay, p.full_name].filter(Boolean).join('   ')
    text(metaLine, nameX, yy - 9.5, { size: 6, color: GREY })
  }

  function rowsThatFit(currentY) {
    return Math.max(1, Math.floor((currentY - FOOTER_RESERVE) / ROW_H))
  }

  let idx = 0
  while (idx < sheetPlayers.length) {
    const availableRows = rowsThatFit(y)
    const perPage = availableRows * 2
    const pagePlayers = sheetPlayers.slice(idx, idx + perPage)
    const half = Math.ceil(pagePlayers.length / 2)
    const colPlayers = [pagePlayers.slice(0, half), pagePlayers.slice(half)]
    const startY = y
    let maxRows = 0
    colPlayers.forEach((list, colIdx) => {
      let ry = startY
      const cx = colIdx === 0 ? col1X : col2X
      list.forEach(p => { drawPlayerRow(p, cx, ry); ry -= ROW_H })
      maxRows = Math.max(maxRows, list.length)
    })
    y = startY - maxRows * ROW_H - 6
    idx += perPage
    if (idx < sheetPlayers.length) { addPage(); drawHeader(true) }
  }

  // Managers section
  if (managers && managers.length > 0) {
    if (y < 160) addPage()
    y -= 6
    page.drawLine({ start: { x: MARGIN, y }, end: { x: PAGE_W - MARGIN, y }, thickness: 0.6, color: LIGHT_GREY })
    y -= 20
    text('Bainisteoirí', PAGE_W / 2 - 30, y, { size: 13, bold: true })
    y -= 20

    const half2 = Math.ceil(managers.length / 2)
    const mgrCols = [managers.slice(0, half2), managers.slice(half2)]
    const mgrStartY = y
    mgrCols.forEach((list, colIdx) => {
      let ry = mgrStartY
      const cx = colIdx === 0 ? col1X : col2X
      list.forEach(m => {
        const irishName = m.full_name_irish?.trim() || m.full_name
        text(irishName, cx, ry, { size: 9, bold: true })
        const idDisplay = m.foireann_id || '__________'
        text([idDisplay, m.full_name].filter(Boolean).join('   '), cx, ry - 10, { size: 6.5, color: GREY })
        ry -= 24
      })
    })
  }

  // Substitution slips — one per non-starting player, for handing to the ref
  const subsList = sheetPlayers.filter(p => p.selected && !p.starting)
  if (subsList.length > 0) {
    const SLIPS_PER_PAGE = 8
    const slipAreaTop = PAGE_H - MARGIN - 34
    const slipAreaBottom = 90
    const slipHeight = (slipAreaTop - slipAreaBottom) / SLIPS_PER_PAGE
    let slipPage, slipIndexOnPage
    function addSlipPage() {
      slipPage = pdfDoc.addPage([PAGE_W, PAGE_H])
      pages.push(slipPage)
      slipPage.drawText('Substitution Slips — ' + (squad?.name || ''), { x: MARGIN, y: PAGE_H - MARGIN + 4, size: 13, font: fontB, color: BLUE })
      slipPage.drawText('vs ' + (fixture?.opponent || '') + '   ' + (fixture?.date || ''), { x: MARGIN, y: PAGE_H - MARGIN - 10, size: 9, font, color: GREY })
      slipIndexOnPage = 0
    }
    addSlipPage()
    subsList.forEach((p) => {
      if (slipIndexOnPage >= SLIPS_PER_PAGE) addSlipPage()
      const slipTop = slipAreaTop - (slipIndexOnPage * slipHeight)
      const slipBottom = slipTop - slipHeight
      slipPage.drawRectangle({ x: MARGIN, y: slipBottom + 4, width: PAGE_W - MARGIN * 2, height: slipHeight - 8, borderColor: LIGHT_GREY, borderWidth: 0.6 })
      slipPage.drawText((club?.club_name || club?.name || '') + ' — Substitution', { x: MARGIN + 8, y: slipTop - 14, size: 10, font: fontB, color: BLUE })
      slipPage.drawText((squad?.name || '') + '   vs ' + (fixture?.opponent || '') + '   ' + (fixture?.date || ''), { x: MARGIN + 8, y: slipTop - 25, size: 7.5, font, color: GREY })
      slipPage.drawText('Coming ON:', { x: MARGIN + 8, y: slipTop - 42, size: 9, font, color: GREY })
      const subName = p.full_name || ''
      slipPage.drawText((p.jersey_number ? '#' + p.jersey_number + '  ' : '') + subName, { x: MARGIN + 78, y: slipTop - 42, size: 13, font: fontB, color: BLACK })
      slipPage.drawText('Coming OFF:', { x: MARGIN + 8, y: slipTop - 60, size: 9, font, color: GREY })
      slipPage.drawLine({ start: { x: MARGIN + 78, y: slipTop - 63 }, end: { x: PAGE_W - MARGIN - 8, y: slipTop - 63 }, thickness: 0.6, color: LIGHT_GREY })
      slipIndexOnPage++
    })
  }

  // Footer + page numbers on every page
  pages.forEach((pg, i) => {
    pg.drawLine({ start: { x: MARGIN, y: 64 }, end: { x: PAGE_W - MARGIN, y: 64 }, thickness: 0.6, color: LIGHT_GREY })
    pg.drawText('(Sínithe ag an Rúnaí)', { x: MARGIN, y: 50, size: 7, font, color: GREY })
    pg.drawText('(Sínithe ag an Réiteoir)', { x: 320, y: 50, size: 7, font, color: GREY })
    pg.drawText('Generated via GAA Match Tracker · ' + new Date().toLocaleDateString('en-IE'), { x: MARGIN, y: 20, size: 7, font, color: GREY })
    pg.drawText('Page ' + (i + 1) + ' of ' + pages.length, { x: PAGE_W - MARGIN - 60, y: 20, size: 7, font, color: GREY })
  })

  return await pdfDoc.save()
}

/**
 * Fetches the bundled fonts as ArrayBuffers, ready to pass into generateTeamSheetPdf.
 */
export async function loadTeamSheetFonts() {
  const [regular, bold] = await Promise.all([
    fetch('/fonts/DejaVuSans.ttf').then(r => r.arrayBuffer()),
    fetch('/fonts/DejaVuSans-Bold.ttf').then(r => r.arrayBuffer())
  ])
  return { fontRegular: regular, fontBold: bold }
}

/**
 * Fetches an image (club logo, GAA crest) as bytes for embedding.
 * Returns null if the fetch fails — caller should generate the PDF without it in that case.
 */
export async function loadLogoBytes(url) {
  if (!url) return null
  try {
    const res = await fetch(url)
    if (!res.ok) return null
    const buf = await res.arrayBuffer()
    const type = url.toLowerCase().includes('.jpg') || url.toLowerCase().includes('.jpeg') ? 'jpg' : 'png'
    return { bytes: buf, type }
  } catch (e) {
    return null
  }
}